import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import clinicAPI from "../../../services/clinicApi";
import { formatDateRu, formatTimeRu } from "../../../utils/appointmentLabels";
import {
  DEFAULT_ATTENDANCE_OPTIONS,
  defaultClosesAtFromDatetime,
  defaultClosesAtFromSession,
  pollTypeLabel,
} from "../../../utils/pollDisplay";
import s from "../clinic.module.css";
import { useAdminHubUi } from "../../../hooks/useAdminAppMode";
import aa from "../AdminPortal/adminApp.module.css";
import { HubFormCard, HubListGroup, HubNavAdd, HubRow, HubScreen, HubSegment, HubSheet, HubSheetActions } from "../AdminPortal/HubPwa";
import MailingsPanel from "./MailingsPanel";
import SwipeableRow from "../AdminPortal/SwipeableRow";
import {
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  Segment,
  Select,
  useToast,
} from "../../ui";

const POLL_TYPE_OPTIONS = [
  { id: "attendance", label: "Явка" },
  { id: "feedback", label: "Отзыв" },
  { id: "custom", label: "Анкета" },
];

const TARGET_MODE_OPTIONS = [
  { id: "datetime", label: "Указать время" },
  { id: "session", label: "Из расписания" },
];

function emptyDraft() {
  const defaultTarget = new Date();
  defaultTarget.setDate(defaultTarget.getDate() + 1);
  defaultTarget.setHours(18, 0, 0, 0);
  const pad = (n) => String(n).padStart(2, "0");
  const targetDefault = `${defaultTarget.getFullYear()}-${pad(defaultTarget.getMonth() + 1)}-${pad(defaultTarget.getDate())}T18:00`;

  return {
    title: "",
    description: "",
    poll_type: "attendance",
    question: "Придёте на тренировку?",
    options: DEFAULT_ATTENDANCE_OPTIONS.map((o) => ({ ...o })),
    group_ids: [],
    target_mode: "datetime",
    session_id: "",
    target_session_at: targetDefault,
    closes_at: defaultClosesAtFromDatetime(targetDefault),
    send_mode: "manual",
    hours_before: 2,
  };
}

function PollEditor({
  draft,
  onChange,
  groups,
  sessions,
  onClose,
  onSave,
  saving,
  isEdit,
  isApp = false,
}) {
  const set = (patch) => onChange({ ...draft, ...patch });

  const toggleGroup = (id) => {
    const gid = String(id);
    const has = draft.group_ids.includes(gid);
    set({
      group_ids: has
        ? draft.group_ids.filter((x) => x !== gid)
        : [...draft.group_ids, gid],
    });
  };

  const updateOption = (idx, patch) => {
    const options = draft.options.map((o, i) =>
      i === idx ? { ...o, ...patch } : o,
    );
    set({ options });
  };

  const addOption = () => {
    const n = draft.options.length + 1;
    set({
      options: [
        ...draft.options,
        { id: `o${n}`, text: "", forecast: draft.poll_type === "attendance" ? "unknown" : null },
      ],
    });
  };

  const onSessionPick = (sessionId) => {
    const sess = sessions.find((x) => x.id === sessionId);
    set({
      session_id: sessionId,
      target_mode: "session",
      target_session_at: "",
      closes_at: sess
        ? defaultClosesAtFromSession(sess.start_datetime)
        : draft.closes_at,
    });
  };

  const onTargetDatetime = (value) => {
    set({
      target_mode: "datetime",
      session_id: "",
      target_session_at: value,
      closes_at: value
        ? defaultClosesAtFromDatetime(value)
        : draft.closes_at,
    });
  };

  const setSendMode = (mode) => {
    if (mode === "auto") {
      set({ send_mode: "auto", session_id: "", target_session_at: "" });
      return;
    }
    set({ send_mode: "manual" });
  };

  const title = isEdit ? "Редактировать опрос" : "Создать опрос";
  const primaryLabel = saving
    ? "Отправка…"
    : isEdit
      ? "Сохранить"
      : draft.send_mode === "auto"
        ? "Создать правило"
        : "Создать и отправить";

  const formClass = isApp ? aa.pollEditorSheetForm : s.pollEditorForm;

  const fields = (
    <div className={formClass}>
      <HubFormCard title="Основное">
        <Field label="Название">
          <Input
            value={draft.title}
            onChange={(e) => set({ title: e.target.value })}
            placeholder="Явка на среду"
            autoFocus
          />
        </Field>
        <Field label="Тип">
          <Segment
            options={POLL_TYPE_OPTIONS}
            value={draft.poll_type}
            onChange={(poll_type) => {
              set({
                poll_type,
                options:
                  poll_type === "attendance"
                    ? DEFAULT_ATTENDANCE_OPTIONS.map((o) => ({ ...o }))
                    : [
                        { id: "o1", text: "Вариант 1", forecast: null },
                        { id: "o2", text: "Вариант 2", forecast: null },
                      ],
              });
            }}
          />
        </Field>
        <Field label="Вопрос">
          <Input
            value={draft.question}
            onChange={(e) => set({ question: e.target.value })}
            placeholder="Придёте на тренировку?"
          />
        </Field>
      </HubFormCard>

      <HubFormCard title="Варианты ответа">
        <ul className={aa.pollOptionList}>
          {draft.options.map((opt, idx) => (
            <li key={opt.id || idx} className={aa.pollOptionRow}>
              <Input
                value={opt.text}
                onChange={(e) => updateOption(idx, { text: e.target.value })}
                placeholder={`Вариант ${idx + 1}`}
              />
              {draft.poll_type === "attendance" ? (
                <Select
                  value={opt.forecast || "unknown"}
                  onChange={(e) =>
                    updateOption(idx, { forecast: e.target.value })
                  }
                  aria-label="Прогноз"
                >
                  <option value="coming">Приду</option>
                  <option value="not_coming">Не приду</option>
                  <option value="unknown">Не знаю</option>
                </Select>
              ) : null}
            </li>
          ))}
        </ul>
        <Button type="button" size="sm" variant="secondary" onClick={addOption}>
          + Вариант
        </Button>
      </HubFormCard>

      <HubFormCard title="Рассылка">
        <div className={aa.pollSendModeCards}>
          <button
            type="button"
            className={
              draft.send_mode === "manual"
                ? aa.pollSendModeCardOn
                : aa.pollSendModeCard
            }
            onClick={() => setSendMode("manual")}
          >
            <strong>Один раз сейчас</strong>
            <small>Отправить клиентам выбранных групп</small>
          </button>
          <button
            type="button"
            className={
              draft.send_mode === "auto"
                ? aa.pollSendModeCardOn
                : aa.pollSendModeCard
            }
            onClick={() => setSendMode("auto")}
          >
            <strong>Перед каждой тренировкой</strong>
            <small>Автоматически за N часов до занятия</small>
          </button>
        </div>

        {draft.send_mode === "auto" ? (
          <Field label="За сколько часов до тренировки">
            <Select
              value={String(draft.hours_before || 2)}
              onChange={(e) => set({ hours_before: Number(e.target.value) })}
            >
              <option value="2">2 часа</option>
              <option value="4">4 часа</option>
              <option value="6">6 часов</option>
              <option value="12">12 часов</option>
              <option value="24">24 часа</option>
            </Select>
          </Field>
        ) : (
          <>
            <Field label="К какому времени">
              <Segment
                options={TARGET_MODE_OPTIONS}
                value={draft.target_mode}
                onChange={(target_mode) => {
                  if (target_mode === "session") {
                    set({ target_mode, target_session_at: "" });
                  } else {
                    const next =
                      draft.target_session_at ||
                      emptyDraft().target_session_at;
                    set({
                      target_mode,
                      session_id: "",
                      target_session_at: next,
                      closes_at: defaultClosesAtFromDatetime(next),
                    });
                  }
                }}
              />
            </Field>
            {draft.target_mode === "datetime" ? (
              <Field
                label="Дата и время тренировки"
                hint="Опрос уйдёт на ближайшее занятие в расписании к этому времени"
              >
                <Input
                  type="datetime-local"
                  value={draft.target_session_at}
                  onChange={(e) => onTargetDatetime(e.target.value)}
                />
              </Field>
            ) : (
              <Field
                label="Занятие из расписания"
                hint="Можно оставить пустым — возьмём ближайшее по каждой группе"
              >
                <Select
                  value={draft.session_id}
                  onChange={(e) => onSessionPick(e.target.value)}
                >
                  <option value="">Ближайшее по каждой группе</option>
                  {sessions.map((sess) => (
                    <option key={sess.id} value={sess.id}>
                      {sess.group?.name || "Группа"} ·{" "}
                      {formatDateRu(sess.start_datetime)}{" "}
                      {formatTimeRu(sess.start_datetime)}
                    </option>
                  ))}
                </Select>
              </Field>
            )}
            <Field label="Дедлайн ответов">
              <Input
                type="datetime-local"
                value={draft.closes_at}
                onChange={(e) => set({ closes_at: e.target.value })}
              />
            </Field>
          </>
        )}
      </HubFormCard>

      <HubFormCard title="Группы">
        <div className={aa.pollGroupGrid}>
          {groups.length === 0 ? (
            <p className={aa.hubHint}>
              Сначала создайте группу в разделе «Группы».
            </p>
          ) : (
            groups.map((g) => {
              const on = draft.group_ids.includes(String(g.id));
              return (
                <button
                  key={g.id}
                  type="button"
                  className={on ? aa.pollGroupChipOn : aa.pollGroupChip}
                  aria-pressed={on}
                  onClick={() => toggleGroup(g.id)}
                >
                  {g.name}
                </button>
              );
            })
          )}
        </div>
      </HubFormCard>

      <aside className={isApp ? aa.pollEditorSheetHints : s.pollEditorHints}>
        {draft.send_mode === "auto" ? (
          <p>
            Перед каждым занятием выбранных групп клиенты получат push. Дедлайн —
            за час до начала.
          </p>
        ) : draft.target_mode === "datetime" ? (
          <p>
            Укажите дату и время тренировки — система найдёт занятие в
            расписании и отправит опрос участникам группы.
          </p>
        ) : (
          <p>
            Можно выбрать конкретное занятие или отправить на ближайшее по
            каждой группе.
          </p>
        )}
      </aside>
    </div>
  );

  if (isApp) {
    return (
      <HubSheet
        open
        onClose={onClose}
        title={title}
        titleId="poll-editor-title"
        bodyClassName={aa.pollEditorSheetBody}
        footer={
          <HubSheetActions
            primary={primaryLabel}
            onSecondary={onClose}
            onPrimary={onSave}
            primaryDisabled={saving}
          />
        }
      >
        <div className={aa.pollEditorSheetForm}>{fields}</div>
      </HubSheet>
    );
  }

  return (
    <div className={s.pollsEditorBackdrop} onClick={onClose} role="presentation">
      <aside
        className={`${s.pollsEditor} ${s.pollsEditor_wide}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="poll-editor-title"
      >
        <header className={s.pollsEditorHead}>
          <h3 id="poll-editor-title">{title}</h3>
          <button
            type="button"
            className={s.pollsEditorClose}
            onClick={onClose}
            aria-label="Закрыть"
          >
            ×
          </button>
        </header>

        <div className={s.pollsEditorBody}>{fields}</div>

        <footer className={s.pollsEditorFoot}>
          <Button type="button" variant="secondary" onClick={onClose}>
            Отмена
          </Button>
          <Button type="button" onClick={onSave} disabled={saving}>
            {primaryLabel}
          </Button>
        </footer>
      </aside>
    </div>
  );
}

function PollResults({ pollId, onClose, isApp = false }) {
  const toast = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    clinicAPI
      .getTrainingPollResults(pollId)
      .then(setData)
      .catch((e) => {
        toast.error(e.response?.data?.error || "Не удалось загрузить");
        onClose();
      })
      .finally(() => setLoading(false));
  }, [pollId, onClose, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const extend = async () => {
    try {
      await clinicAPI.extendTrainingPoll(pollId, { hours: 2 });
      toast.success("Дедлайн продлён на 2 часа");
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
    }
  };

  if (loading) {
    if (isApp) {
      return (
        <HubSheet open onClose={onClose} title="Итоги опроса">
          <p className={aa.hubHint}>Загрузка…</p>
        </HubSheet>
      );
    }
    return (
      <div className={s.pollsEditorBackdrop} onClick={onClose} role="presentation">
        <aside className={s.pollsEditor} onClick={(e) => e.stopPropagation()}>
          <p className={s.pollsEditorIntro}>Загрузка…</p>
        </aside>
      </div>
    );
  }

  const poll = data?.poll;
  const stats = data?.stats || {};
  const roster = data?.roster || [];
  const isAttendance = poll?.poll_type === "attendance";

  const resultsBody = (
    <>
      <div className={isApp ? aa.pollResultsBody : s.pollsEditorBody}>
        <div className={s.pollsStatsRow}>
          <span>
            Отправлено: <strong>{stats.sent ?? 0}</strong>
          </span>
          <span>
            Ответили: <strong>{stats.responded ?? 0}</strong>
          </span>
          <span>
            Ждут: <strong>{stats.pending ?? 0}</strong>
          </span>
          {isAttendance && stats.forecast_coming != null ? (
            <span className={s.pollsForecastBadge}>
              Прогноз «приду»: <strong>{stats.forecast_coming}</strong>
            </span>
          ) : null}
        </div>

        {(stats.by_option || []).length > 0 ? (
          <ul className={s.pollsOptionStats}>
            {stats.by_option.map((o) => (
              <li key={o.id}>
                {o.text}: <strong>{o.count}</strong>
              </li>
            ))}
          </ul>
        ) : null}

        {roster.length === 0 ? (
          <EmptyState title="Пока нет ответов" description="Клиенты увидят опрос в личном кабинете" />
        ) : (
          <ul className={s.pollsRosterList}>
            {roster.map((r) => (
              <li
                key={r.dispatch_id}
                className={`${s.pollsRosterRow} ${s[`pollsRosterRow_${r.status}`]}`}
              >
                <span className={s.pollsRosterName}>{r.full_name}</span>
                <span className={s.pollsRosterStatus}>{r.status_label}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {!isApp ? (
        <footer className={s.pollsEditorFoot}>
          {poll?.status === "active" ? (
            <Button type="button" variant="secondary" onClick={extend}>
              Продлить на 2 ч
            </Button>
          ) : null}
          <Button type="button" onClick={onClose}>
            Закрыть
          </Button>
        </footer>
      ) : (
        <div className={aa.pollResultsFoot}>
          {poll?.status === "active" ? (
            <button type="button" className={aa.hubSecondaryCta} onClick={extend}>
              Продлить на 2 ч
            </button>
          ) : null}
        </div>
      )}
    </>
  );

  if (isApp) {
    return (
      <HubSheet
        open
        onClose={onClose}
        title={poll?.title || "Итоги"}
        subtitle={pollTypeLabel(poll?.poll_type)}
      >
        {resultsBody}
      </HubSheet>
    );
  }

  return (
    <div className={s.pollsEditorBackdrop} onClick={onClose} role="presentation">
      <aside
        className={`${s.pollsEditor} ${s.pollsEditor_wide}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
      >
        <header className={s.pollsEditorHead}>
          <div>
            <h3>{poll?.title}</h3>
            <p className={s.pollsCardMeta}>{pollTypeLabel(poll?.poll_type)}</p>
          </div>
          <button
            type="button"
            className={s.pollsEditorClose}
            onClick={onClose}
            aria-label="Закрыть"
          >
            ×
          </button>
        </header>
        {resultsBody}
      </aside>
    </div>
  );
}

export default function AdminPolls() {
  const toast = useToast();
  const isApp = useAdminHubUi();
  const [polls, setPolls] = useState([]);
  const [groups, setGroups] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(emptyDraft);
  const [saving, setSaving] = useState(false);
  const [resultsId, setResultsId] = useState(null);
  const [section, setSection] = useState("polls");
  const [mailingEditorOpen, setMailingEditorOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await clinicAPI.getTrainingPolls();
      setPolls(data.polls || []);
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка загрузки");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
    clinicAPI
      .getSportGroupsSummary()
      .then((r) => setGroups(r.groups || []))
      .catch(() => {});
  }, [load]);

  useEffect(() => {
    if (!draft.group_ids.length) {
      setSessions([]);
      return;
    }
    const from = new Date().toISOString().slice(0, 10);
    const toDate = new Date();
    toDate.setDate(toDate.getDate() + 45);
    const to = toDate.toISOString().slice(0, 10);
    clinicAPI
      .getTrainingSessions({ from, to })
      .then((r) => {
        const gset = new Set(draft.group_ids.map(String));
        setSessions(
          (r.sessions || []).filter((sess) => {
            const gid = sess.group?.id || sess.specialization_id;
            return gid && gset.has(String(gid));
          }),
        );
      })
      .catch(() => setSessions([]));
  }, [draft.group_ids]);

  const activePolls = useMemo(
    () => polls.filter((p) => p.status === "active"),
    [polls],
  );

  const openCreate = () => {
    setEditingId(null);
    setDraft(emptyDraft());
    setEditorOpen(true);
  };

  const openEdit = (poll) => {
    setEditingId(poll.id);
    setDraft({
      title: poll.title || "",
      description: poll.description || "",
      poll_type: poll.poll_type || "attendance",
      question: poll.questions?.[0]?.text || "",
      options:
        poll.options?.length > 0
          ? poll.options.map((o, i) => ({
              id: o.id || `o${i + 1}`,
              text: o.text || "",
              forecast: o.forecast || null,
            }))
          : DEFAULT_ATTENDANCE_OPTIONS.map((o) => ({ ...o })),
      group_ids: (poll.group_ids || []).map(String),
      target_mode: poll.target_session_at ? "datetime" : "session",
      session_id: poll.session_id || "",
      target_session_at: poll.target_session_at
        ? String(poll.target_session_at).slice(0, 16)
        : "",
      closes_at: poll.closes_at
        ? String(poll.closes_at).slice(0, 16)
        : "",
      send_mode: poll.send_mode || "manual",
      hours_before: poll.hours_before || 2,
    });
    setEditorOpen(true);
  };

  const savePoll = async () => {
    if (!draft.title.trim()) {
      toast.error("Укажите название");
      return;
    }
    if (!draft.group_ids.length) {
      toast.error("Выберите группы");
      return;
    }
    if (draft.send_mode === "manual" && draft.target_mode === "datetime") {
      if (!draft.target_session_at) {
        toast.error("Укажите дату и время тренировки");
        return;
      }
    }
    const options = draft.options.filter((o) => o.text.trim());
    if (!options.length) {
      toast.error("Добавьте варианты ответа");
      return;
    }
    setSaving(true);
    try {
      const manual = draft.send_mode !== "auto";
      const payload = {
        title: draft.title.trim(),
        description: draft.description.trim() || null,
        poll_type: draft.poll_type,
        question: draft.question.trim(),
        options,
        group_ids: draft.group_ids,
        session_id:
          manual && draft.target_mode === "session"
            ? draft.session_id || null
            : null,
        target_session_at:
          manual && draft.target_mode === "datetime" && draft.target_session_at
            ? new Date(draft.target_session_at).toISOString()
            : null,
        closes_at: draft.closes_at
          ? new Date(draft.closes_at).toISOString()
          : null,
        send_mode: draft.send_mode === "auto" ? "auto" : "manual",
        hours_before: Number(draft.hours_before) || 2,
        publish: draft.send_mode !== "auto",
      };
      if (editingId) {
        await clinicAPI.updateTrainingPoll(editingId, {
          ...payload,
          republish: false,
        });
        toast.success("Опрос обновлён");
      } else {
        const r = await clinicAPI.createTrainingPoll(payload);
        if (draft.send_mode === "auto") {
          toast.success(
            "Опрос включён — будет отправляться перед каждой тренировкой выбранных групп",
          );
        } else {
          toast.success(
            r.sent
              ? `Опрос создан, отправлено ${r.sent} клиентам`
              : "Опрос создан",
          );
        }
      }
      setEditorOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка сохранения");
    } finally {
      setSaving(false);
    }
  };

  const removePoll = async (poll) => {
    if (!window.confirm(`Удалить опрос «${poll.title}»?`)) return;
    try {
      await clinicAPI.deleteTrainingPoll(poll.id);
      toast.success("Удалено");
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
    }
  };

  const pollCountLabel = `${polls.length} ${
    polls.length === 1 ? "опрос" : polls.length < 5 ? "опроса" : "опросов"
  }`;

  const pollsContent = (
    <>
      {loading ? (
        <Card>
          <p className={s.pollsEditorIntro}>Загрузка…</p>
        </Card>
      ) : polls.length === 0 ? (
        isApp ? (
          <EmptyState
            title="Пока нет опросов"
            description="Создайте опрос явки — клиенты ответят в приложении"
            actionLabel="Создать опрос"
            onAction={openCreate}
          />
        ) : (
        <Card>
          <EmptyState
            title="Пока нет опросов"
            description="Создайте опрос явки — клиенты ответят в личном кабинете, а вы увидите прогноз до занятия."
            actionLabel="Создать опрос"
            onAction={openCreate}
          />
        </Card>
        )
      ) : isApp ? (
        <>
          {activePolls.length > 0 ? (
            <section>
              <h2 className={aa.hubSectionLabel}>Активные</h2>
              <HubListGroup>
                {activePolls.map((poll) => (
                  <SwipeableRow
                    key={poll.id}
                    actions={[
                      {
                        id: "results",
                        label: "Итоги",
                        tone: "neutral",
                        onClick: () => setResultsId(poll.id),
                      },
                      {
                        id: "edit",
                        label: "Изменить",
                        tone: "neutral",
                        onClick: () => openEdit(poll),
                      },
                      {
                        id: "delete",
                        label: "Удалить",
                        tone: "danger",
                        onClick: () => removePoll(poll),
                      },
                    ]}
                  >
                    <HubRow
                      title={poll.title}
                      meta={pollTypeLabel(poll.poll_type)}
                      sub={
                        poll.send_mode === "auto"
                          ? `Авто · за ${poll.hours_before || 2} ч`
                          : `Ответили ${poll.stats?.responded ?? 0} из ${poll.stats?.sent ?? 0}`
                      }
                      badge={
                        poll.stats?.forecast_coming != null
                          ? `${poll.stats.forecast_coming} приду`
                          : undefined
                      }
                      badgeTone="accent"
                      onClick={() => setResultsId(poll.id)}
                    />
                  </SwipeableRow>
                ))}
              </HubListGroup>
            </section>
          ) : null}
          {polls.filter((p) => p.status !== "active").length > 0 ? (
            <section>
              <h2 className={aa.hubSectionLabel}>Завершённые</h2>
              <HubListGroup>
                {polls
                  .filter((p) => p.status !== "active")
                  .map((poll) => (
                    <HubRow
                      key={poll.id}
                      title={poll.title}
                      meta={`${pollTypeLabel(poll.poll_type)} · ответили ${poll.stats?.responded ?? 0}`}
                      onClick={() => setResultsId(poll.id)}
                    />
                  ))}
              </HubListGroup>
            </section>
          ) : null}
        </>
      ) : (
        <>
          {activePolls.length > 0 ? (
            <section className={s.pollsActiveSection}>
              <h2 className={s.pollsSectionTitle}>Активные</h2>
              <ul className={s.pollsCardList}>
                {activePolls.map((poll) => (
                  <li key={poll.id}>
                    <Card className={s.pollsCard}>
                      <div className={s.pollsCardTop}>
                        <div>
                          <span className={s.pollsTypeBadge}>
                            {pollTypeLabel(poll.poll_type)}
                          </span>
                          <h3 className={s.pollsCardTitle}>{poll.title}</h3>
                        </div>
                        {poll.poll_type === "attendance" &&
                        poll.stats?.forecast_coming != null ? (
                          <div className={s.pollsCardForecast}>
                            <span className={s.pollsCardForecastVal}>
                              {poll.stats.forecast_coming}
                            </span>
                            <span className={s.pollsCardForecastLbl}>
                              приду
                            </span>
                          </div>
                        ) : null}
                      </div>
                      <p className={s.pollsCardMeta}>
                        {poll.send_mode === "auto"
                          ? `Авто · за ${poll.hours_before || 2} ч до тренировки`
                          : "Разовая отправка"}
                        {" · "}
                        Ответили {poll.stats?.responded ?? 0} из{" "}
                        {poll.stats?.sent ?? 0}
                        {poll.send_mode === "manual" && poll.closes_at
                          ? ` · до ${formatDateRu(poll.closes_at)} ${formatTimeRu(poll.closes_at)}`
                          : ""}
                      </p>
                      <div className={s.pollsCardActions}>
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => setResultsId(poll.id)}
                        >
                          Результаты
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="secondary"
                          onClick={() => openEdit(poll)}
                        >
                          Изменить
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="secondary"
                          onClick={() => removePoll(poll)}
                        >
                          Удалить
                        </Button>
                      </div>
                    </Card>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {polls.filter((p) => p.status !== "active").length > 0 ? (
            <section>
              <h2 className={s.pollsSectionTitle}>Завершённые</h2>
              <ul className={s.pollsCardList}>
                {polls
                  .filter((p) => p.status !== "active")
                  .map((poll) => (
                    <li key={poll.id}>
                      <Card className={s.pollsCard}>
                        <h3 className={s.pollsCardTitle}>{poll.title}</h3>
                        <p className={s.pollsCardMeta}>
                          {pollTypeLabel(poll.poll_type)} · ответили{" "}
                          {poll.stats?.responded ?? 0}
                        </p>
                        <Button
                          type="button"
                          size="sm"
                          variant="secondary"
                          onClick={() => setResultsId(poll.id)}
                        >
                          Смотреть
                        </Button>
                      </Card>
                    </li>
                  ))}
              </ul>
            </section>
          ) : null}
        </>
      )}

      {isApp ? null : (
        <p className={s.pollsFooterHint}>
          Опросы обратной связи не влияют на прогноз.{" "}
          <Link to="/manage/app/schedule">Календарь</Link> показывает ответы по
          занятию.
        </p>
      )}

      {editorOpen ? (
        <PollEditor
          draft={draft}
          onChange={setDraft}
          groups={groups}
          sessions={sessions}
          onClose={() => setEditorOpen(false)}
          onSave={savePoll}
          saving={saving}
          isEdit={Boolean(editingId)}
          isApp={isApp}
        />
      ) : null}

      {resultsId ? (
        <PollResults pollId={resultsId} onClose={() => setResultsId(null)} isApp={isApp} />
      ) : null}
    </>
  );

  const sectionTabs = [
    { id: "polls", label: "Опросы" },
    { id: "mailings", label: "Рассылки" },
  ];

  const handleAdd = () => {
    if (section === "mailings") setMailingEditorOpen(true);
    else openCreate();
  };

  if (isApp) {
    return (
      <HubScreen
        title="Опросы"
        right={
          <HubNavAdd
            onClick={handleAdd}
            ariaLabel={section === "mailings" ? "Создать рассылку" : "Создать опрос"}
          />
        }
      >
        <HubSegment tabs={sectionTabs} active={section} onChange={setSection} />
        {section === "polls" ? (
          <>
            {polls.length > 0 ? (
              <span className={aa.hubCount}>{pollCountLabel}</span>
            ) : null}
            {pollsContent}
          </>
        ) : (
          <MailingsPanel
            groups={groups}
            editorOpen={mailingEditorOpen}
            onEditorOpenChange={setMailingEditorOpen}
          />
        )}
      </HubScreen>
    );
  }

  return (
    <div className={s.pollsPage}>
      <header className={s.pollsPageHead}>
        <div>
          <h1 className={s.pollsPageTitle}>Опросы и рассылки</h1>
          <p className={s.pollsPageSub}>
            Опросы явки рассылаются автоматически или вручную. Во вкладке
            «Рассылки» — push-сообщения без вопросов: объявления, переносы,
            напоминания.
          </p>
        </div>
        <Button type="button" onClick={handleAdd}>
          {section === "mailings" ? "+ Создать рассылку" : "+ Создать опрос"}
        </Button>
      </header>
      <HubSegment tabs={sectionTabs} active={section} onChange={setSection} />
      {section === "polls" ? pollsContent : (
        <MailingsPanel
          groups={groups}
          editorOpen={mailingEditorOpen}
          onEditorOpenChange={setMailingEditorOpen}
        />
      )}
    </div>
  );
}
